/* ==========================================================================
   voxel-sea-turtle.js — the build half, vendored verbatim apart from exports.

   A turtle does not paddle, it FLIES. The foreflipper is a wing, and thrust
   comes from the blade twisting out of phase with the sweep: on the
   downstroke the leading edge pitches down so the resultant force tilts
   forward, and on the recovery it pitches the other way so the upstroke
   pushes too. A flat plate flapped up and down produces almost nothing. That
   quarter-cycle lead between twist and sweep is the entire mechanism.

   The stroke is a rotating BASIS, not a displacement. Each flipper voxel is
   baked with its station along the blade, its chord offset and its thickness,
   and the vertex shader rebuilds the blade's frame at that station — so the
   tip can lag the root and the whole wing flexes, all closed form, all on
   the GPU, one draw call. TURTLE_STROKE_GLSL below is that evaluator, kept
   in this file so the parameterisation and the code reading it cannot drift.
   ========================================================================== */
'use strict';

const TAU = Math.PI * 2;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
function sstep(e0, e1, x) { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function makeNoise(seed) {
  const r = mulberry32(seed >>> 0), P = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) P[i] = r();
  function h(x, y, z) {
    let n = (x * 374761393 + y * 668265263 + z * 1442695041) | 0;
    n = ((n ^ (n >> 13)) * 1274126177) | 0;
    return P[(n >>> 0) & 1023];
  }
  return function (x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const a = lerp(h(xi, yi, zi), h(xi + 1, yi, zi), u), b = lerp(h(xi, yi + 1, zi), h(xi + 1, yi + 1, zi), u);
    const c = lerp(h(xi, yi, zi + 1), h(xi + 1, yi, zi + 1), u), d = lerp(h(xi, yi + 1, zi + 1), h(xi + 1, yi + 1, zi + 1), u);
    return lerp(lerp(a, b, v), lerp(c, d, v), w);
  };
}
function fbm(nz, x, y, z, oct, gain) {
  let a = 1, f = 1, s = 0, n = 0; gain = gain || 0.5;
  for (let i = 0; i < oct; i++) { s += a * (nz(x * f, y * f, z * f) - 0.5) * 2; n += a; a *= gain; f *= 2.03; }
  return s / n;
}
const mixc = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const mulc = (c, k) => [c[0] * k, c[1] * k, c[2] * k];

/* voxel roles — the shader picks pigment from these */
const R_SHELL = 0, R_SEAM = 1, R_PLASTRON = 2, R_SKIN = 3, R_FLIP = 4, R_EYE = 5, R_BEAK = 6;

function buildTurtle(st) {
  const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
  const vox = st.vox, seed = st.seed, span = st.span, dome = st.dome, belly = st.belly, taper = st.taper;
  const nz = makeNoise(seed * 7919 + 11);

  const px = [], py = [], pz = [], fl = [], fs = [], fc = [], ft = [], role = [], cr = [], cg = [], cb = [], shd = [];
  let nShell = 0, nFlip = 0;
  const scutes = new Set();

  /* ---- carapace geometry ----------------------------------------------
     A green turtle's shell is a rounded teardrop: broad over the shoulders,
     narrowing to a blunt point at the tail. */
  const CL = 0.68, CW = 0.545, CH = 0.395 * dome;    /* half length/width, height */
  const shellR = (u) => {                            /* half-width at along u=-1..1 */
    const a = clamp(u, -1, 1);
    return CW * Math.sqrt(Math.max(0, 1 - a * a)) * (1 + 0.16 * a - 0.10 * a * a) * (1 - 0.06 * Math.cos(a * 3.1));
  };
  /* Longitudinal profile. A real carapace crowns just FORWARD of centre and
     falls to roughly a quarter of its height at the neck notch and at the
     tail; the falloff is asymmetric because the front drops away harder. */
  const TP = 0.55 + 0.55 * taper;
  const lonProf = (u) => {
    const t = u - 0.06;                              /* crown sits slightly forward */
    const k = (t > 0 ? 0.86 : 0.68) * TP;            /* front steeper than back     */
    return Math.max(0.05, 1 - k * Math.pow(Math.abs(t) / 1.06, 1.55));
  };
  const shellTop = (u, vN) => {
    const prof = Math.sqrt(Math.max(0, 1 - vN * vN));
    return CH * prof * lonProf(u);
  };
  /* The plastron bulges downward and meets the carapace at a thin rim, so the
     shell is a LENS. Closing it with a flat plane makes a dome sitting on a
     disc, which reads as a hard machined edge all the way round. */
  const CB = 0.125 * belly;
  const shellBot = (u, vN) => {
    const prof = Math.pow(Math.max(0, 1 - vN * vN), 0.62);
    return -0.030 - CB * prof * lerp(0.42, 1.0, lonProf(u));
  };
  const insideShell = (x, y, z) => {
    const u = z / CL; if (Math.abs(u) > 1) return false;
    const r = shellR(u); if (r < 1e-4) return false;
    const vN = x / r; if (Math.abs(vN) > 1) return false;
    return y >= shellBot(u, vN) && y <= shellTop(u, vN);
  };

  /* ---- scute layout ---------------------------------------------------
     Five vertebral scutes down the midline, four costal pairs either side,
     and a ring of marginals. Baked at build time: the shell never deforms. */
  const VERT = 5, COST = 4, MARG = 11;
  function scuteAt(u, vN) {
    const av = Math.abs(vN);
    let band, idx, cu, cv;
    if (av < 0.30) { band = 0; idx = Math.floor((u * 0.5 + 0.5) * VERT); cu = (u * 0.5 + 0.5) * VERT - idx; cv = av / 0.30; }
    else if (av < 0.78) { band = 1; idx = Math.floor((u * 0.5 + 0.5) * COST); cu = (u * 0.5 + 0.5) * COST - idx; cv = (av - 0.30) / 0.48; }
    else { band = 2; idx = Math.floor((u * 0.5 + 0.5) * MARG); cu = (u * 0.5 + 0.5) * MARG - idx; cv = (av - 0.78) / 0.22; }
    const side = vN < 0 ? 0 : 1;
    const id = band * 1000 + idx * 10 + (band === 0 ? 0 : side);
    const du = Math.min(cu, 1 - cu), dv = Math.min(cv, 1 - cv);
    return { id, band, idx, side, cu, cv, edge: Math.min(du, dv * 1.35) };
  }

  const lo = -CW - vox * 2, hiZ = CL + vox * 2;
  for (let z = -hiZ; z <= hiZ; z += vox)
    for (let x = lo; x <= -lo; x += vox)
      for (let y = -0.34; y <= CH + vox * 2; y += vox) {
        if (!insideShell(x, y, z)) continue;
        if (insideShell(x + vox, y, z) && insideShell(x - vox, y, z) && insideShell(x, y + vox, z) &&
          insideShell(x, y - vox, z) && insideShell(x, y, z + vox) && insideShell(x, y, z - vox)) continue;
        const u = z / CL, r = shellR(u), vN = r > 1e-4 ? x / r : 0;
        const tp = shellTop(u, vN), bt = shellBot(u, vN);
        /* Fraction through the shell: 0 at the plastron, 1 at the carapace. A
           fixed height cutoff cannot work once the underside curves, because
           the bottom sits far lower amidships than at the rim. */
        const frac = (y - bt) / Math.max(tp - bt, 1e-4);
        let R, c;
        if (frac < 0.32) {                             /* plastron */
          const pu = (u * 0.5 + 0.5) * 6, pi = Math.floor(pu), pf = pu - pi;
          const mid = Math.abs(vN) < 0.026;
          const pseam = Math.min(pf, 1 - pf) < 0.050 || mid;
          const h2 = mulberry32((pi * 37 + (vN < 0 ? 1 : 2)) * 613 + seed)();
          R = pseam ? R_SEAM : R_PLASTRON;
          c = [0.745, 0.712, 0.560];
          c = mulc(c, 0.93 + 0.13 * h2);
          c = mixc(c, [0.620, 0.575, 0.430], sstep(0.55, 0.95, frac / 0.32) * 0.55);
          c = mulc(c, 1 + (nz(x * 11, y * 11, z * 11) - 0.5) * 0.10);
        } else {
          const S = scuteAt(u, vN);
          scutes.add(S.id);
          const seam = S.edge < 0.052;
          R = seam ? R_SEAM : R_SHELL;
          /* Radiating streaks inside each scute. Green turtle scutes are
             marked with pale rays fanning from one corner — the single most
             recognisable thing about the shell after its outline. */
          const h1 = mulberry32(S.id * 9176 + seed)();
          const ang = Math.atan2(S.cv - 0.15, S.cu - 0.12);
          const ray = 0.5 + 0.5 * Math.sin(ang * (5 + Math.floor(h1 * 4)) + h1 * 6.28);
          const rad = Math.hypot(S.cu - 0.12, S.cv - 0.15);
          const base = [0.255, 0.205, 0.120], warm = [0.560, 0.395, 0.175];
          c = mixc(base, warm, clamp(ray * sstep(0.10, 0.85, rad), 0, 1));
          c = mulc(c, 0.86 + 0.28 * h1);
          c = mulc(c, 1 + (nz(x * 9, y * 9, z * 9) - 0.5) * 0.13);
        }
        px.push(x); py.push(y); pz.push(z);
        fl.push(-1); fs.push(0); fc.push(0); ft.push(0); role.push(R);
        cr.push(c[0]); cg.push(c[1]); cb.push(c[2]);
        shd.push(0.90 + 0.20 * nz(x * 17, y * 17, z * 17));
        nShell++;
      }

  /* ---- head and neck --------------------------------------------------- */
  const HZ = CL + 0.170, HY = 0.070, HR = 0.128;
  const insideHead = (x, y, z) => {
    const dz = (z - HZ) / 1.35, dy = (y - HY) / 0.92;
    const d = Math.hypot(x / 0.86, dy, dz);
    if (d < HR) return true;
    const t = (z - (CL - 0.03)) / 0.20;
    if (t >= 0 && t <= 1) { const nr = lerp(0.128, 0.100, t); return Math.hypot(x, (y - lerp(0.055, HY, t))) < nr; }
    return false;
  };
  for (let z = CL - 0.06; z <= HZ + HR + vox * 2; z += vox)
    for (let x = -0.20; x <= 0.20; x += vox)
      for (let y = -0.10; y <= 0.26; y += vox) {
        if (!insideHead(x, y, z)) continue;
        if (insideHead(x + vox, y, z) && insideHead(x - vox, y, z) && insideHead(x, y + vox, z) &&
          insideHead(x, y - vox, z) && insideHead(x, y, z + vox) && insideHead(x, y, z - vox)) continue;
        let R = R_SKIN;
        let c = [0.300, 0.290, 0.225];
        const pl = fbm(nz, x * 26, y * 26, z * 26, 2) * 0.5 + 0.5;
        c = mixc(c, [0.680, 0.630, 0.470], sstep(0.46, 0.84, pl) * 0.90);
        for (const s of [1, -1]) {
          const dx = x - s * 0.090, dy = y - (HY + 0.036), dz = z - (HZ + 0.036);
          if (Math.hypot(dx * 0.85, dy, dz * 0.80) < 0.044) {
            R = (Math.abs(dy) < vox * 1.1) ? R_EYE : R_SKIN;
            if (R === R_SKIN) c = [0.480, 0.390, 0.230];
          }
        }
        if (z > HZ + HR * 0.62 && y < HY + 0.012) { R = R_BEAK; c = [0.700, 0.650, 0.500]; }
        px.push(x); py.push(y); pz.push(z);
        fl.push(-1); fs.push(0); fc.push(0); ft.push(0); role.push(R);
        cr.push(c[0]); cg.push(c[1]); cb.push(c[2]);
        shd.push(0.90 + 0.20 * nz(x * 17, y * 17, z * 17));
        nShell++;
      }

  /* ---- tail ------------------------------------------------------------ */
  for (let z = -CL - 0.10; z <= -CL + 0.03; z += vox) {
    const t = clamp((-CL + 0.03 - z) / 0.13, 0, 1), r = lerp(0.055, 0.014, t);
    for (let x = -r; x <= r; x += vox) for (let y = -0.02; y <= r * 1.4; y += vox) {
      if (Math.hypot(x, (y - 0.012) / 1.3) > r) continue;
      px.push(x); py.push(y + 0.010); pz.push(z);
      fl.push(-1); fs.push(0); fc.push(0); ft.push(0); role.push(R_SKIN);
      cr.push(0.235); cg.push(0.230); cb.push(0.180);
      shd.push(0.92 + 0.16 * nz(x * 17, y * 17, z * 17));
      nShell++;
    }
  }

  /* ---- flippers, baked in blade parameter space ------------------------
     Fore: long and wing-like, widest a quarter out, tapering to a point.
     Rear: short paddles that act mostly as rudders. */
  const FL = 0.92 * span, RL = 0.36 * span;
  function blade(idx, len, chordFn, thickFn, root) {
    const NS = Math.max(6, Math.round(len / vox));
    for (let i = 0; i <= NS; i++) {
      const s = i / NS, ch = chordFn(s);
      if (ch < vox * 0.6) continue;
      const NC = Math.max(2, Math.round(ch * 2 / vox));
      for (let j = 0; j <= NC; j++) {
        const c = (j / NC - 0.5) * 2 * ch;
        const th = thickFn(s, c / Math.max(ch, 1e-4));
        const NT = Math.max(1, Math.round(th * 2 / vox));
        for (let k = 0; k <= NT; k++) {
          const t = NT > 0 ? (k / NT - 0.5) * 2 * th : 0;
          /* surface only: skip interior */
          if (i > 0 && i < NS && j > 1 && j < NC - 1 && NT > 2 && k > 0 && k < NT) continue;
          let col = [0.225, 0.222, 0.176];
          const pl = fbm(nz, s * 18 + idx * 7, c * 30, t * 30, 2) * 0.5 + 0.5;
          col = mixc(col, [0.560, 0.525, 0.400], sstep(0.55, 0.90, pl) * 0.80);
          /* pale trailing edge and tip, as on a real flipper */
          col = mixc(col, [0.680, 0.650, 0.520], sstep(0.72, 1.0, Math.abs(c) / Math.max(ch, 1e-4)) * 0.55);
          col = mixc(col, [0.700, 0.670, 0.545], sstep(0.86, 1.0, s) * 0.65);
          px.push(root[0]); py.push(root[1]); pz.push(root[2]);
          fl.push(idx); fs.push(s); fc.push(c); ft.push(t); role.push(R_FLIP);
          cr.push(col[0]); cg.push(col[1]); cb.push(col[2]);
          shd.push(0.90 + 0.20 * nz(s * 40, c * 40, t * 40));
          nFlip++;
        }
      }
    }
  }
  const foreChord = s => 0.135 * span * (0.55 + 1.35 * Math.sin(Math.pow(clamp(s, 0, 1), 0.62) * Math.PI * 0.92)) * (1 - 0.55 * s * s);
  const foreThick = (s, cn) => 0.030 * span * (1 - 0.62 * s) * (1 - 0.55 * Math.abs(cn));
  const rearChord = s => 0.115 * span * (0.75 + 0.9 * Math.sin(Math.pow(clamp(s, 0, 1), 0.7) * Math.PI)) * (1 - 0.45 * s);
  const rearThick = (s, cn) => 0.028 * span * (1 - 0.5 * s) * (1 - 0.5 * Math.abs(cn));
  /* Roots sit OUT at the shell rim and on the same side the shader extends
     toward. Getting either wrong makes the flipper sprout from inside the
     carapace and sweep across the body: aFlip 0 and 2 take side -x, 1 and 3
     take +x, so their roots must have matching sign. */
  const SHX = 0.405, SHY = 0.026, SHZ = 0.285;       /* fore shoulder */
  const HPX = 0.330, HPY = 0.006, HPZ = -0.360;      /* rear hip      */
  blade(0, FL, foreChord, foreThick, [-SHX, SHY, SHZ]);
  blade(1, FL, foreChord, foreThick, [SHX, SHY, SHZ]);
  blade(2, RL, rearChord, rearThick, [-HPX, HPY, HPZ]);
  blade(3, RL, rearChord, rearThick, [HPX, HPY, HPZ]);

  const n = px.length;
  return {
    n, nShell, nFlip, scutes: scutes.size, vox, foreLen: FL, rearLen: RL,
    x: new Float32Array(px), y: new Float32Array(py), z: new Float32Array(pz),
    fl: new Float32Array(fl), fs: new Float32Array(fs),
    fc: new Float32Array(fc), ft: new Float32Array(ft),
    role: new Float32Array(role), shade: new Float32Array(shd),
    cr: new Float32Array(cr), cg: new Float32Array(cg), cbl: new Float32Array(cb),
    ms: (typeof performance !== 'undefined' ? performance : Date).now() - t0
  };
}

/* --------------------------------------------------------------------------
   The stroke, as a rotating basis.

   E1 runs out along the blade, E2 is the chord axis, E3 is up. Sweeping
   rotates the blade about E2, the fore-aft shuttle about the new up axis,
   and the twist about the blade's own axis. Evaluating all three at the
   voxel's own station lets the tip lag the root, so the wing flexes through
   the stroke instead of staying rigid.
   -------------------------------------------------------------------------- */
const TURTLE_STROKE_GLSL = `
uniform float uStroke,uSweep,uTwist,uLead,uFore,uLag,uBack,uBank,uFold,uFLen,uRLen;
attribute float aFlip,aS,aC,aT,aRole,aShade;
attribute vec3 aCol;

void flipperPos(float f,float s,float c,float th,vec3 root,out vec3 P){
  float side = (mod(f,2.0) < 0.5) ? -1.0 : 1.0;
  float rear = (f >= 1.5) ? 1.0 : 0.0;
  float len  = mix(uFLen,uRLen,rear);

  /* the tip trails the root by up to a radian of phase — that lag is what
     makes a flexible wing look flexible */
  float ph = uStroke - uLag*s*mix(1.0,0.45,rear) + rear*2.15;
  float amp = mix(1.0,0.30,rear)*(1.0 + side*uBank*0.40);

  float sw = uSweep*amp*sin(ph);
  /* twist LEADS the sweep by a quarter cycle; without that offset the blade
     is a flat plate and generates no thrust at all */
  float tw = uTwist*mix(1.0,0.45,rear)*sin(ph + uLead);
  float fo = uFore*mix(1.0,0.5,rear)*sin(2.0*ph)*(-side);

  /* resting: flippers fold back and down against the shell */
  sw = mix(sw,-0.30,uFold);
  tw = mix(tw, 0.10,uFold);
  float back = uBack + uFold*0.85;

  float cbk=cos(back), sbk=sin(back);
  vec3 E1 = normalize(vec3(side*cbk,0.0,-sbk));
  vec3 E3 = vec3(0.0,1.0,0.0);
  vec3 E2 = normalize(cross(E3,E1));

  float cs=cos(sw), ss=sin(sw);
  vec3 A1 = E1*cs + E3*ss;
  vec3 A3 = -E1*ss + E3*cs;

  float cf=cos(fo), sf=sin(fo);
  vec3 B1 = A1*cf + E2*sf;
  vec3 B2 = -A1*sf + E2*cf;

  float ct=cos(tw), stw=sin(tw);
  vec3 C2 = B2*ct + A3*stw;
  vec3 C3 = -B2*stw + A3*ct;

  P = root + B1*(s*len) + C2*c + C3*th;
}
`;

export {
  buildTurtle, TURTLE_STROKE_GLSL,
  R_SHELL, R_SEAM, R_PLASTRON, R_SKIN, R_FLIP, R_EYE, R_BEAK,
};
export default { buildTurtle, TURTLE_STROKE_GLSL };
